import React, {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useContext,
} from "react";
import styled from "styled-components";
import { RaangeDataTableContext } from "./RaangeDataTable";
import { RaangeDataTableDirectionFormatter } from "./RaangeDataTableFormatters";

export type RaangeDataTableSortDirection = "asc" | "desc";
export type RaangeDataTableSortStrategy<T> = (a: T, b: T) => number;

type SortStrategyType<T> = {
  sortStrategy: RaangeDataTableSortStrategy<T>;
};
export type IRaangeDataTableSortProps<T> = {
  [K in keyof T]: {
    sortKey: K;
    children: ReactNode;
  } & (NonNullable<T[K]> extends string
    ? Partial<SortStrategyType<T[K]>>
    : SortStrategyType<T[K]>);
}[keyof T];

export default <T,>({
  sortKey,
  sortStrategy,
  children,
}: IRaangeDataTableSortProps<T>) => {
  const dataTableContext = useContext(RaangeDataTableContext);
  if (!dataTableContext) {
    throw new Error("NoOp: RaangeDataTableContext is not provided");
  }
  const { sortSettings, setSortSettings } = dataTableContext;
  const { sortKey: tableSortKey, sortDirection: tableSortDirection } =
    sortSettings;

  const handleSortClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();

      let newSortKey: string | undefined = tableSortKey;
      let newSortDirection: RaangeDataTableSortDirection | undefined =
        tableSortDirection;

      // If new sort key is used, reset direction.
      if (tableSortKey !== sortKey) {
        newSortKey = sortKey as string;
        newSortDirection = "asc";
      } else {
        if (tableSortDirection === "asc") {
          newSortDirection = "desc";
        } else if (tableSortDirection === "desc") {
          newSortDirection = undefined;
          newSortKey = undefined;
        } else {
          newSortDirection = "asc";
        }
      }

      setSortSettings({
        sortKey: newSortKey,
        sortDirection: newSortDirection,
        sortStrategy,
      });
    },
    [sortKey, sortStrategy, tableSortKey, tableSortDirection, setSortSettings],
  );

  if (!dataTableContext) {
    return null;
  }

  return (
    <SortStyled href="###" onClick={handleSortClick}>
      {children}
      {tableSortKey === sortKey && (
        <RaangeDataTableDirectionFormatter>
          {tableSortDirection}
        </RaangeDataTableDirectionFormatter>
      )}
    </SortStyled>
  );
};

const SortStyled = styled.a`
  display: block;
  width: 100%;
  height: 100%;
  color: inherit;

  &:hover {
    text-decoration: none;
    color: inherit;
  }
`;
